import React from 'react';
import { Helmet } from 'react-helmet-async';
import { CheckCircle2, Award, Target, Briefcase, Eye, Code, User } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

export const About = () => {
  const stats = [
    { number: "3+", label: "Years Experience in fertility hospital analytics, clinical data analysis, and healthcare research" },
    { number: "Clinical", label: "Project experience spanning IVF cycle analysis, hospital performance data, oncology datasets, and patient-level operational analytics" },
    { number: "Specialized", label: "Domain expertise in UAE healthcare: DHA, DOH, and MOH regulatory frameworks, IVF clinical workflows, and fertility hospital data environments" },
    { number: "Advanced", label: "Statistical toolkit covering R, Python, SPSS, STATA, machine learning, predictive modelling, and interactive dashboard development" }
  ];

  const expertise = [
    'Biostatistics in reproductive medicine and fertility research',
    'IVF outcome analysis and performance evaluation',
    'Clinical, hospital, and observational data analysis',
    'Questionnaire and survey data analysis',
    'Oncology data analysis (including survival analysis)',
    'Data quality assurance and regulatory compliance',
    'Advanced statistical modeling and predictive analytics',
    'Dashboard development and healthcare reporting'
  ];

  const backgroundPoints = [
    'Cycle-level IVF outcome analysis and protocol performance evaluation',
    'Patient retention and operational performance analytics for UAE clinics',
    'Hospital patient satisfaction survey analysis using factor analysis and multivariate regression',
    'Oncology survival analysis using Kaplan-Meier, Cox regression, and competing risks methods',
    'Predictive modelling for clinical risk stratification using machine learning',
    'KPI dashboard development for clinical and management audiences',
    'Regulatory reporting support for DHA, DOH, and international frameworks including ESHRE and ISAR'
  ];

  const values = [
    {
      icon: Target,
      title: 'Precision',
      description: 'Every analysis is built on the right method for the specific question, not the most familiar method, or the quickest one. Statistical rigour is non-negotiable because the decisions that follow from it are clinical ones.'
    },
    {
      icon: Briefcase,
      title: 'Clinical Understanding',
      description: 'We have worked inside UAE fertility hospitals and healthcare settings, not just analysed data from them. That means we understand clinical workflows, regulatory expectations, and what decision-makers actually need from an analytics output.'
    },
    {
      icon: Eye,
      title: 'Clarity',
      description: 'The output of every engagement is something a clinician or manager can act on. Not a methodology document. Not a statistical appendix. A clear, specific answer to the question that was asked, presented in language that lands with the people who need to use it.'
    },
    {
      icon: Award,
      title: 'Excellence',
      description: 'High standards at every stage, from data cleaning and validation through to final reporting. Because an insight built on poor data quality is not an insight. It is a risk.'
    }
  ];

  const teamMembers = [
    {
      name: "Sahana Chandrappa",
      title: "Biostatistician – Reproductive & IVF Clinical Research Analytics",
      description: "Specializing in reproductive health and fertility research, with extensive clinical research experience working with IVF data and research teams in fertility hospital settings.",
      focus: [
        "IVF outcome analysis and prediction",
        "Clinical research in reproductive medicine",
        "Treatment effectiveness evaluation",
        "Clinical data interpretation and research methodology"
      ],
      bio: "Sahana founded StatZen Analytics after 3+ years working as a biostatistician inside a UAE fertility hospital, building outcome reports, supporting clinical teams with cycle-level data analysis, and developing the regulatory reporting infrastructure that IVF clinics in the UAE are required to maintain. She has worked directly with IVF cycle data, patient-level records, and clinical research teams in a hospital setting, not in a consultancy looking in from the outside. That experience is the foundation of everything StatZen delivers."
    },
    {
      name: "Naveenkumar R",
      title: "Biostatistician – Clinical & Research Analytics",
      description: "Experienced in healthcare data analysis across hospitals, oncology datasets, and academic research.",
      focus: [
        "Questionnaire and survey data analysis",
        "Oncology and clinical data analysis",
        "Thesis and research methodology support",
        "Statistical modeling and interpretation"
      ],
      bio: "Naveen brings clinical and research analytics experience spanning hospital data, oncology datasets, and academic research support. He works with healthcare professionals and research teams to ensure that data is structured correctly, analysed rigorously, and presented in a form that supports decision-making and publication. His background in questionnaire analysis, survival methods, and statistical modelling complements Sahana's IVF and fertility domain expertise, giving StatZen the coverage to work across the full range of UAE healthcare and research contexts."
    }
  ];

  const tools = [
    { category: "Statistical Software", icon: "📊", items: ["R", "SPSS", "SAS"] },
    { category: "Programming", icon: "💻", items: ["Python", "SQL"] },
    { category: "BI & Visualization", icon: "📈", items: ["Qlik Sense", "Other BI Tools"] },
    { category: "Data Management", icon: "📋", items: ["Excel", "Databases"] }
  ];

  return (
    <div className="min-h-screen">
      <Helmet>
        <title>About Us - StatZen Analytics</title>
        <link rel="canonical" href="https://statzenanalytics.com/about" />
      </Helmet>
      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About StatZen Analytics
            </h1>
            <p className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto mb-4">
              StatZen Analytics was built on a simple observation: most healthcare clinics in the UAE collect more data than they know what to do with, and very few have the statistical infrastructure to turn that data into decisions that actually change outcomes.
            </p>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We are a UAE-based healthcare analytics consultancy founded by a biostatistician with over 3 years of hands-on experience working inside a UAE fertility hospital, analysing real cycle data, supporting real clinical teams, and understanding firsthand what the gap between data and decision-making actually costs a clinic. We work with IVF clinics, hospitals, polyclinics, and research teams across the UAE to close that gap.
            </p>
          </div>

          {/* Key Highlights */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-8">Key Highlights</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {stats.map((stat, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <div className="text-3xl font-bold text-blue-600 mb-2">
                    {stat.number}
                  </div>
                  <div className="text-gray-700">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                Our Mission
              </h2>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                To help UAE healthcare providers and research teams find what their data is actually telling them and act on it.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                We believe the gap between clinical data and clinical decision-making is one of the most underleveraged problems in UAE healthcare. Most clinics have the data. Very few have the analytical infrastructure to turn it into a ranked list of what to fix, in what order, and why.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                That is what StatZen Analytics is built to do. Not reports for the sake of reports. Not methodology for the sake of methodology. Analytics that changes what a clinic director decides tomorrow morning.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=800&q=80"
                alt="Data Analytics and Statistics"
                className="rounded-lg shadow-xl"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Background Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Our Background
          </h2>
          <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              StatZen Analytics was founded after 3+ years of working inside a UAE fertility hospital, not as a consultant looking in from the outside, but as part of the clinical analytics team. That experience involved working directly with IVF cycle data, supporting clinical decision-making with outcome analysis, building reporting systems for regulatory submissions, and understanding how clinical teams actually use (and often don't use) the data available to them.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              That insider perspective is what makes StatZen different from a general statistics consultancy. We don't need to be briefed on what AMH is, why cycle cancellation rate matters, or what a DHA compliance report requires. We've built them. We know where the gaps are in how most clinics currently manage their data because we've sat inside those gaps.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed mb-4">
              The work since then has extended across hospital performance analytics, patient satisfaction research, oncology survival analysis, predictive modelling, and KPI dashboard development across UAE clinic types and patient populations.
            </p>
            <p className="text-lg text-gray-700 mb-4">Our work has involved:</p>
            <ul className="space-y-3 mb-6">
              {backgroundPoints.map((point, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 rounded-full bg-blue-600 flex-shrink-0 mt-2" />
                  <span className="text-gray-700">{point}</span>
                </li>
              ))}
            </ul>
            <p className="text-lg text-gray-700 leading-relaxed">
              Every engagement starts with your data and your question. The statistical method follows from both, not the other way around.
            </p>
          </div>
        </div>
      </section>

      {/* Expertise Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Areas of Expertise
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto">
            {expertise.map((item, index) => (
              <div
                key={index}
                className="flex items-start space-x-4 bg-gray-50 p-6 rounded-lg hover:shadow-md transition-shadow duration-300"
              >
                <CheckCircle2 className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
                <span className="text-gray-700 leading-relaxed">{item}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Our Approach Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Approach
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              We follow a structured and practical approach to every project
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6 text-center">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="w-8 h-8 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">
                    {value.title}
                  </h3>
                  <p className="text-gray-600 text-sm">
                    {value.description}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Specialized Focus */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Why Domain Expertise Changes Everything
          </h2>
          <p className="text-lg text-blue-100 leading-relaxed mb-6">
            There is a meaningful difference between a statistician who analyses healthcare data and one who has worked inside a healthcare environment.
          </p>
          <p className="text-lg text-blue-100 leading-relaxed mb-6">
            The first knows how to run the analysis. The second knows why the cancellation happened at day 8 rather than day 6, what an AMH of 0.4 ng/mL means for stimulation planning, and why the compliance report needs to be structured the way the DHA expects it, not the way the analysis software produces it by default.
          </p>
          <p className="text-lg text-blue-100 leading-relaxed">
            StatZen Analytics brings both. The statistical rigour to execute analyses that hold up to peer review or regulatory scrutiny. The clinical domain knowledge to ask the right questions before the analysis begins and interpret the answers in a way that is useful to the people who need to act on them.
          </p>
        </div>
      </section>

      {/* Meet the Team */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Meet the Team
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              A collaborative team of biostatisticians bringing together expertise in reproductive 
              health, clinical data analytics, and research methodology
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-8 max-w-6xl mx-auto">
            {teamMembers.map((member, index) => (
              <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow duration-300 w-full lg:w-[calc(50%-1rem)] max-w-lg">
                <CardContent className="p-8">
                  <div className="text-center mb-6">
                    {member.name === "Sahana Chandrappa" ? (
                      <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-blue-100">
                        <img 
                          src="/sahana-photo.jpg" 
                          alt="Sahana Chandrappa" 
                          className="w-full h-full object-cover"
                          style={{ objectPosition: 'center center' }}
                        />
                      </div>
                    ) : member.name === "Naveenkumar R" ? (
                      <div className="w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-blue-100">
                        <img 
                          src="/naveen-photo.jpg" 
                          alt="Naveenkumar R" 
                          className="w-full h-full object-cover"
                          style={{ objectPosition: 'center center' }}
                        />
                      </div>
                    ) : (
                      <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <User className="w-10 h-10 text-blue-600" />
                      </div>
                    )}
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {member.name}
                    </h3>
                    <p className="text-blue-600 font-semibold mb-4">
                      {member.title}
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      {member.description}
                    </p>
                  </div>

                  <div className="bg-blue-50 p-6 rounded-lg mb-6">
                    <h4 className="font-semibold text-gray-900 mb-3">Focused on:</h4>
                    <ul className="space-y-2">
                      {member.focus.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-2">
                          <div className="w-1.5 h-1.5 rounded-full bg-blue-600 flex-shrink-0 mt-2" />
                          <span className="text-gray-700 text-sm">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <p className="text-gray-600 leading-relaxed italic">
                    {member.bio}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Our Combined Strength */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6 text-center">
            Our Combined Strength
          </h2>
          <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
            <p className="text-lg text-gray-700 leading-relaxed text-center">
              Sahana brings deep IVF and fertility hospital domain expertise, the kind that comes from working inside a clinical environment rather than consulting into one. Naveen brings broad healthcare and research analytics experience across hospitals, oncology, and academic settings.
              <br/><br/>
              Together that means StatZen Analytics can take on the full range of UAE healthcare analytics work, from cycle-level IVF outcome analysis and regulatory reporting through to hospital performance dashboards, patient satisfaction research, survival analysis for clinical publications, and predictive modelling for risk stratification, without losing the domain depth that makes the analysis meaningful.
              <br/><br/>
              We are not a generalist statistics firm that happens to serve healthcare. We are a healthcare analytics consultancy that happens to be exceptionally good at statistics.
            </p>
          </div>
        </div>
      </section>

      {/* Tools & Technologies */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Code className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Tools & Technologies
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Proficient in industry-standard tools for statistical analysis, data management, and visualization
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {tools.map((toolGroup, index) => (
              <Card key={index} className="border-gray-200 hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                    <span className="text-2xl">{toolGroup.icon}</span>
                  </div>
                  <h3 className="font-semibold text-gray-900 mb-4">{toolGroup.category}</h3>
                  <ul className="space-y-2">
                    {toolGroup.items.map((item, idx) => (
                      <li key={idx} className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full bg-blue-600" />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Additional Info */}
          <div className="mt-12 text-center">
            <p className="text-gray-600 max-w-3xl mx-auto">
              We work across the complete data workflow, from raw data cleaning and validation through statistical modelling, visualisation, and reporting. Every output is built to the standard required by the audience it is going to: regulatory-ready for DHA and DOH submissions, publication-ready for peer-reviewed journals, and management-ready for clinic directors and hospital COOs.
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
